import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { EndpointDTO } from 'src/components/types';
import { environmentService } from 'src/providers/environment/EnvironmentService';
import { CreateAxiosClients } from 'src/providers/session/helpers/axiosHelper';
import { SignInResponseDTO } from 'src/providers/session/types';

export interface HttpClient {
  get: <_Req, Res>(url: string, controller?: AbortController) => Promise<AxiosResponse<Res>>;
  post: <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Res>>;
  postSignIn: <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Res>>;
  postSignOut: <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Res>>;
  put: <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Res>>;
  upload: <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
    config?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<Res>>;
  _delete: <_Req, Res>(url: string, controller?: AbortController) => Promise<AxiosResponse<Res>>;
  sendRequest: <Req, Res>(
    endpoint: EndpointDTO,
    body?: Req,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Res>>;
}

export function useHttpClient(renewSession: () => Promise<SignInResponseDTO>): HttpClient {
  const { axiosClient, axiosClientUpload, axiosClientSignInSignOut } = CreateAxiosClients({
    renewSession,
  });

  const buildUrl = (url: string) => `${environmentService.getEnvironment()?.apiBase}${url}`;

  const get = async <_Req, Res>(url: string, controller?: AbortController) =>
    axiosClient.get<_Req, AxiosResponse<Res>>(
      buildUrl(url),
      controller ? { signal: controller.signal } : {},
    );

  const post = async <Req, Res>(url: string, body?: Req, controller?: AbortController) =>
    axiosClient.post<Res>(
      buildUrl(url),
      body || null,
      controller ? { signal: controller.signal } : {},
    );

  const postSignIn = async <Req, Res>(url: string, body?: Req, controller?: AbortController) =>
    axiosClientSignInSignOut.post<Res>(
      buildUrl(url),
      body || null,
      controller ? { signal: controller.signal } : {},
    );

  const postSignOut = async <Req, Res>(url: string, body?: Req, controller?: AbortController) =>
    axiosClientSignInSignOut.post<Res>(
      buildUrl(url),
      body || null,
      controller ? { signal: controller.signal } : {},
    );

  const put = async <Req, Res>(url: string, body?: Req, controller?: AbortController) =>
    axiosClient.put<Res>(
      buildUrl(url),
      body || null,
      controller ? { signal: controller.signal } : {},
    );

  const upload = async <Req, Res>(
    url: string,
    body?: Req,
    controller?: AbortController,
    config?: AxiosRequestConfig,
  ) =>
    axiosClientUpload.post<Res>(buildUrl(url), body || null, {
      ...(controller ? { signal: controller.signal } : {}),
      ...(config || {}),
    });

  const _delete = async <_Req, Res>(url: string, controller?: AbortController) =>
    axiosClient.delete<_Req, AxiosResponse<Res>>(
      buildUrl(url),
      controller ? { signal: controller.signal } : {},
    );

  const sendRequest = async <Req, Res>(
    endpoint: EndpointDTO,
    body?: Req,
    controller?: AbortController,
  ) => {
    const method = endpoint.method.toLowerCase();
    if (method === 'get') return get<Req, Res>(endpoint.url, controller);
    if (method === 'put') return put<Req, Res>(endpoint.url, body, controller);
    return post<Req, Res>(endpoint.url, body, controller);
  };

  return { get, post, postSignIn, postSignOut, put, upload, _delete, sendRequest };
}
